import React from 'react';
import { FileText, Building } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'icon' | 'wordmark' | 'full';
  className?: string;
}

const sizeClasses = {
  sm: {
    icon: 'w-6 h-6',
    text: 'text-sm'
  },
  md: {
    icon: 'w-8 h-8', 
    text: 'text-xl'
  },
  lg: {
    icon: 'w-12 h-12',
    text: 'text-2xl'
  },
  xl: {
    icon: 'w-16 h-16',
    text: 'text-3xl'
  }
};

export const OptimizedLogo = React.memo<LogoProps>(({ 
  size = 'md', 
  variant = 'full',
  className 
}) => {
  const IconComponent = ({ className: iconClassName }: { className?: string }) => (
    <div className={cn(
      'gradient-primary rounded-lg flex items-center justify-center',
      sizeClasses[size].icon,
      iconClassName
    )}>
      <FileText className={cn(
        'text-white',
        size === 'sm' ? 'h-3 w-3' :
        size === 'md' ? 'h-5 w-5' :
        size === 'lg' ? 'h-6 w-6' : 'h-8 w-8'
      )} />
    </div>
  );

  const WordMark = ({ className: textClassName }: { className?: string }) => (
    <span className={cn(
      'font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent',
      sizeClasses[size].text,
      textClassName
    )}>
      BillPortal
    </span>
  );

  if (variant === 'icon') {
    return <IconComponent className={className} />;
  }

  if (variant === 'wordmark') {
    return <WordMark className={className} />;
  }

  // Full variant
  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <IconComponent />
      <WordMark />
    </div>
  );
});

OptimizedLogo.displayName = 'OptimizedLogo';

// Alternative logo variants for different contexts
export const LogoVariants = {
  // Modern B-letter logo for headers
  BrandIcon: React.memo(({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) => (
    <div className={cn(
      'bg-gradient-to-br from-primary via-primary to-primary/80 rounded-xl flex items-center justify-center shadow-lg',
      size === 'sm' ? 'w-8 h-8' :
      size === 'md' ? 'w-10 h-10' : 'w-12 h-12',
      className
    )}>
      <span className={cn(
        'font-black text-white',
        size === 'sm' ? 'text-sm' :
        size === 'md' ? 'text-lg' : 'text-xl'
      )}>
        B
      </span>
    </div>
  )),

  // Minimal logo for loading states
  MinimalLogo: React.memo(({ className }: { className?: string }) => (
    <div className={cn('flex items-center space-x-1', className)}>
      <div className="w-6 h-6 bg-primary rounded-md" />
      <span className="text-sm font-semibold text-foreground">BillPortal</span>
    </div>
  )),

  // Enhanced logo with building icon for enterprise contexts
  EnterpriseLogo: React.memo(({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) => (
    <div className={cn('flex items-center space-x-2', className)}>
      <div className={cn(
        'bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center',
        sizeClasses[size].icon
      )}>
        <Building className={cn(
          'text-white',
          size === 'sm' ? 'h-3 w-3' :
          size === 'md' ? 'h-4 w-4' : 'h-6 w-6'
        )} />
      </div>
      <div className="flex flex-col">
        <span className={cn(
          'font-bold text-foreground leading-tight',
          size === 'sm' ? 'text-xs' :
          size === 'md' ? 'text-sm' : 'text-base'
        )}>
          BillPortal
        </span>
        {size !== 'sm' && (
          <span className="text-xs text-muted-foreground leading-tight">
            Enterprise
          </span>
        )}
      </div>
    </div>
  ))
};